import { cn } from "@/lib/utils";
import { forwardRef } from "react";
import { Card, CardTitle } from "./card";
import { Tooltip, TooltipTrigger, TooltipContent } from "./tooltip";
import { InfoCircledIcon } from "@radix-ui/react-icons";

export interface FilterButtonProps
  extends React.HTMLAttributes<HTMLDivElement> {
  isSelected?: boolean;
  children: React.ReactNode;
  number?: number;
  subNumber?: number;
  hasError?: boolean;
  numberLoading?: boolean;
  hasWarning?: boolean;
  tooltip?: string;
}

export const FilterButton = forwardRef<HTMLDivElement, FilterButtonProps>(
  (
    {
      className,
      isSelected = false,
      children,
      onClick,
      number,
      subNumber,
      hasError,
      hasWarning,
      numberLoading = false,
      tooltip,
      ...props
    },
    ref
  ) => {
    return (
      <Card
        className={cn(
          "shrink-0 cursor-pointer transition-all border-2 duration-200 w-[160px] h-16 flex items-start py-2 px-3 relative",
          subNumber !== undefined ? "justify-between" : "justify-between",
          hasError && "border-destructive text-destructive",
          hasWarning && "border-warning text-warning",
          isSelected && "bg-majorelle/5 border-majorelle text-majorelle",
          className
        )}
        onClick={() => onClick?.({} as any)}
        tabIndex={0}
        role="button"
        aria-pressed={isSelected}
        ref={ref}
        {...props}
      >
        <CardTitle
          className={cn("text-sm", subNumber !== undefined && "flex-1 mr-2")}
        >
          {children}
        </CardTitle>
        {(number !== undefined || numberLoading) && (
          <div className="absolute flex items-end text-xs font-semibold bottom-2 right-3 text-muted-foreground">
            {numberLoading ? (
              <div className="w-6 h-4 rounded animate-pulse bg-slate-200 dark:bg-slate-700" />
            ) : (
              <>
                {number}
                {subNumber !== undefined && (
                  <span className="ml-1 text-[10px] text-muted-foreground">
                    ({subNumber})
                  </span>
                )}
              </>
            )}
          </div>
        )}
        {tooltip && (
          <div className="absolutetop-2 right-2">
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <span className="inline-flex items-center cursor-pointer">
                  <InfoCircledIcon className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">{tooltip}</TooltipContent>
            </Tooltip>
          </div>
        )}
      </Card>
    );
  }
);
